import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Fade,
  Skeleton,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Collapse,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { Search as SearchIcon, Refresh, WarningAmber, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { api } from '../services/api';

interface OverdueInstallment {
  installment_number: number;
  amount_owed: number;
  days_overdue: number;
  due_date: string;
}

interface DelinquentItem {
  customer_name: string;
  sale_id: number;
  product_name: string;
  days_overdue: number;
  overdue_amount: number;
  vendor_name?: string;
  overdue_installments: OverdueInstallment[];
}

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const formatDate = (d: string) => {
  const parts = d.split('-');
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function MorososPage() {
  const [delinquents, setDelinquents] = useState<DelinquentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [daysFilter, setDaysFilter] = useState<'all' | '7' | '14' | '28'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchDelinquents();
  }, []);

  const fetchDelinquents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/delinquent');
      setDelinquents(res.data);
    } catch (error) {
      console.error('Error fetching delinquents:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (saleId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(saleId)) next.delete(saleId);
      else next.add(saleId);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let data = delinquents;
    if (daysFilter !== 'all') {
      const minDays = parseInt(daysFilter);
      data = data.filter((d) => d.days_overdue >= minDays);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (d) =>
          d.customer_name.toLowerCase().includes(q) ||
          d.product_name.toLowerCase().includes(q) ||
          (d.vendor_name && d.vendor_name.toLowerCase().includes(q))
      );
    }
    return data;
  }, [delinquents, daysFilter, search]);

  const totalOverdue = useMemo(() => filtered.reduce((sum, d) => sum + d.overdue_amount, 0), [filtered]);
  const paginatedFiltered = useMemo(() => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filtered, page, rowsPerPage]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
        <Skeleton variant="text" width="200px" height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 3 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber sx={{ color: '#EF4444', fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontSize: { xs: '1.3rem', sm: '1.7rem' }, fontWeight: 800, color: 'text.primary' }}>
            Morosos
          </Typography>
        </Box>
        <Tooltip title="Actualizar">
          <IconButton
            onClick={fetchDelinquents}
            sx={{
              width: 40, height: 40,
              backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
            }}
          >
            <Refresh sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6}>
          <Card sx={(t) => ({ background: t.palette.mode === 'dark' ? 'rgba(22,27,46,0.6)' : '#fff', border: '1px solid', borderColor: 'divider' })}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Total morosos</Typography>
              <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, color: '#EF4444' }}>{filtered.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card sx={(t) => ({ background: t.palette.mode === 'dark' ? 'rgba(22,27,46,0.6)' : '#fff', border: '1px solid', borderColor: 'divider' })}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Monto adeudado</Typography>
              <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, color: '#EF4444' }}>{fmt(totalOverdue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar cliente, producto o vendedor..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Tabs
          value={daysFilter}
          onChange={(_, v) => { setDaysFilter(v); setPage(0); }}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { fontSize: '0.8rem', minHeight: 36, py: 0, textTransform: 'none' },
            '& .MuiTabs-indicator': { background: '#EF4444', height: 2 },
          }}
        >
          <Tab label="Todos" value="all" />
          <Tab label="7+ dias" value="7" />
          <Tab label="14+ dias" value="14" />
          <Tab label="28+ dias" value="28" />
        </Tabs>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">Sin morosos que coincidan con el filtro</Typography>
          </Box>
        ) : isMobile ? (
          // Mobile cards
          <Box sx={{ p: 1 }}>
            {paginatedFiltered.map((d, i) => (
              <Fade in timeout={200 + i * 50} key={`${d.sale_id}-${i}`}>
                <Card sx={(t) => ({ mb: 1, borderLeft: '4px solid #EF4444', background: t.palette.mode === 'dark' ? 'rgba(22,27,46,0.6)' : '#fff' })}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{d.customer_name}</Typography>
                      <Chip
                        label={`${d.days_overdue}d`}
                        size="small"
                        sx={{
                          backgroundColor: d.days_overdue > 14 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                          color: d.days_overdue > 14 ? '#EF4444' : '#D97706',
                          fontWeight: 600, fontSize: '0.7rem', height: 22,
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{d.product_name}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#EF4444' }}>{fmt(d.overdue_amount)}</Typography>
                      {d.vendor_name && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{d.vendor_name}</Typography>}
                    </Box>
                    {/* Expandable installment detail */}
                    {d.overdue_installments && d.overdue_installments.length > 0 && (
                      <>
                        <Button
                          size="small"
                          onClick={() => toggleRow(d.sale_id)}
                          endIcon={expandedRows.has(d.sale_id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          sx={{ mt: 1, fontSize: '0.75rem', textTransform: 'none', color: '#EF4444' }}
                        >
                          Ver {d.overdue_installments.length} cuota{d.overdue_installments.length > 1 ? 's' : ''} adeudada{d.overdue_installments.length > 1 ? 's' : ''}
                        </Button>
                        <Collapse in={expandedRows.has(d.sale_id)}>
                          <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid rgba(239,68,68,0.3)' }}>
                            {d.overdue_installments.map((inst) => (
                              <Box key={inst.installment_number} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box>
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Cuota #{inst.installment_number}</Typography>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Venc: {formatDate(inst.due_date)} - {inst.days_overdue}d mora</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#EF4444' }}>{fmt(inst.amount_owed)}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 40 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Dias mora</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Monto adeudado</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Vendedor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFiltered.map((d, i) => (
                  <React.Fragment key={`${d.sale_id}-${i}`}>
                    <TableRow
                      sx={{ cursor: d.overdue_installments?.length ? 'pointer' : 'default', '& > *': { borderBottom: expandedRows.has(d.sale_id) ? 'none' : undefined } }}
                      onClick={() => d.overdue_installments?.length && toggleRow(d.sale_id)}
                    >
                      <TableCell>
                        {d.overdue_installments?.length ? (
                          <IconButton size="small">
                            {expandedRows.has(d.sale_id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.customer_name}</Typography>
                      </TableCell>
                      <TableCell>{d.product_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${d.days_overdue} dias`}
                          size="small"
                          sx={{
                            backgroundColor: d.days_overdue > 14 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                            color: d.days_overdue > 14 ? '#EF4444' : '#D97706',
                            fontWeight: 600, fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#EF4444' }}>{fmt(d.overdue_amount)}</TableCell>
                      <TableCell>{d.vendor_name || '\u2014'}</TableCell>
                    </TableRow>
                    {d.overdue_installments?.length > 0 && (
                      <TableRow>
                        <TableCell sx={{ py: 0 }} colSpan={6}>
                          <Collapse in={expandedRows.has(d.sale_id)} timeout="auto" unmountOnExit>
                            <Box sx={{ mx: 2, my: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Cuota #</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Vencimiento</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Dias mora</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Monto</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {d.overdue_installments.map((inst) => (
                                    <TableRow key={inst.installment_number}>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>#{inst.installment_number}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(inst.due_date)}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>
                                        <Chip
                                          label={`${inst.days_overdue}d`}
                                          size="small"
                                          sx={{
                                            backgroundColor: inst.days_overdue > 14 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                                            color: inst.days_overdue > 14 ? '#EF4444' : '#D97706',
                                            fontWeight: 600, fontSize: '0.7rem', height: 20,
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#EF4444' }}>{fmt(inst.amount_owed)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1))}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            labelRowsPerPage="Filas:"
          />
        )}
      </Paper>
    </Container>
  );
}
